/**
 * 組織レポート v3 の個人スコア算出と組織集約（仕様書 §01〜§03）。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB組織レポートの生成ロジック.html
 *
 * 【現行(orgScore.ts / gb74OrgFactors.ts)との関係】
 *  現行は温存し、こちらを新設する。個人レポート v2 と同じ方針。
 *  設問データ（ハーマン24問・Big5・ONA・離職3問）は現行とまったく同じものを使う。
 *  現行との違いは次の3点だけで、それ以外の式は現行と一致している。
 *
 *   ① つながりが 5変数 → 4変数
 *      「現場で進む力」(O-05・O-06) は仕様でマスク中のため算入しない。
 *   ② 離職の兆しの下位25%が「25パーセンタイル値」→「下位25%の平均」
 *      仕様書§02が正本の誤りとして明記している。パーセンタイル値は1人の値なので、
 *      それより下にいる人の深刻さが反映されない（10人での検証で下位2名を拾えなかった）。
 *   ③ グレードが「GBスコアから」→「3スコアのうち最も低いものから」
 *      GBスコアは年次比較用、グレードは「今どこが弱いか」を答えるもので役割が違う。
 *
 * 【暫定パラメータ】
 *  低スコア判定(2.5)・重み(0.7/0.3・0.6/0.4)は仕様書の確定値。
 *  グレード閾値(80/60/40)も同じ。実測待ちの暫定値はこのファイルには無い。
 */
import { BIG5_FACTORS, getFactorScore, type Gb74Scores } from './gb74Report';

// ===== 定数（仕様書§02・§03）=====

/** 働きがい: この値以下を「低スコア」として数える。 */
export const LOW_SCORE_MAX = 2.5;
/** 働きがい: 低スコア率にかける係数。 */
export const LOW_SCORE_PENALTY = 0.3;
/** つながり: 均等平均と最低変数の重み。 */
export const CONN_EVEN_WEIGHT = 0.7;
export const CONN_MIN_WEIGHT = 0.3;
/** 離職の兆し: 平均と下位25%平均の重み。 */
export const RET_MEAN_WEIGHT = 0.6;
export const RET_BOTTOM_WEIGHT = 0.4;

export type Grade = 'A' | 'B' | 'C' | 'D';

/** 働きがいを構成する Big5 6ファセット（仕様書§01）。 */
const WELLBEING_FACETS = [
  'execution', // 遂行力
  'order', // 秩序性
  'emotionControl', // 感情制御
  'initiative', // 主導性
  'consideration', // 配慮性
  'challenge', // 挑戦性
] as const;

/** つながりの4変数。キーは仕様書§01の呼び名に合わせる。 */
export interface ConnectionVarsV3 {
  /** チーム内の助け合い = (O-01 + O-02) / 2 */
  teamHelp: number;
  /** 相談先の広がり = ((6-O-03) + O-04) / 2 */
  consultReach: number;
  /** 仕事の共有度 = ((6-O-07) + O-08) / 2 */
  workSharing: number;
  /** 部署間のつながり = (O-09 + O-10) / 2 */
  crossDept: number;
}

/** 画面に出す変数名。「評価を上げるために」で最低変数を名指しするのに使う。 */
export const CONNECTION_VAR_LABEL: Record<keyof ConnectionVarsV3, string> = {
  teamHelp: 'チーム内の助け合い',
  consultReach: '相談先の広がり',
  workSharing: '仕事の共有度',
  crossDept: '部署間のつながり',
};

export const CONNECTION_VAR_KEYS: Array<keyof ConnectionVarsV3> = [
  'teamHelp',
  'consultReach',
  'workSharing',
  'crossDept',
];

/** 1人分の組織集約用サブスコア（すべて 1.0〜5.0・高いほど良い）。 */
export interface IndividualV3 {
  wellbeing: number;
  conn: ConnectionVarsV3;
  retention: number;
}

// ===== 汎用の数値処理 =====

/** 中央値。空配列は 0。 */
export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

/** 平均。空配列は 0。 */
export function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * 下位25%の「平均」（仕様書§02の読み替え）。
 * 対象人数は ceil(n / 4) 名。22名なら6名。
 * パーセンタイル値（1人の値）ではないので、深刻な少数がそのまま効く。
 */
export function bottomQuarterMean(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const take = Math.max(1, Math.ceil(s.length / 4));
  return mean(s.slice(0, take));
}

/** 1.0〜5.0 → 0〜100（仕様書§02の共通変換）。 */
export function toScore100(adjusted: number): number {
  const v = ((adjusted - 1.0) / 4.0) * 100;
  return Math.max(0, Math.min(100, Math.round(v)));
}

/** 0〜100 → グレード（80/60/40）。 */
export function toGrade(score100: number): Grade {
  if (score100 >= 80) return 'A';
  if (score100 >= 60) return 'B';
  if (score100 >= 40) return 'C';
  return 'D';
}

/** グレードの日本語表記。 */
export const GRADE_LABEL: Record<Grade, string> = {
  A: '良好',
  B: '注意',
  C: '要対応',
  D: '緊急',
};

// ===== 個人スコアの導出（仕様書§01）=====

/** 5段階の逆転（6 − 回答値）。欠損は null。 */
function reverse5(v: number | undefined): number | null {
  return v == null ? null : 6 - v;
}

/** 欠損を除いた平均。全部欠損なら null。 */
function avgOf(vals: Array<number | null | undefined>): number | null {
  const xs = vals.filter((v): v is number => v != null);
  return xs.length === 0 ? null : xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** 欠損変数は 3.0（中立）で補完する。無回答で不利にも有利にもしない。 */
const NEUTRAL = 3.0;

/** つながり4変数を導出（O-05・O-06 は使わない）。 */
export function deriveConnectionV3(ona: Record<string, number> | undefined): ConnectionVarsV3 {
  const o = (code: string) => ona?.[code];
  const f = (v: number | null) => (v == null ? NEUTRAL : v);
  return {
    teamHelp: f(avgOf([o('O-01'), o('O-02')])),
    consultReach: f(avgOf([reverse5(o('O-03')), o('O-04')])),
    workSharing: f(avgOf([reverse5(o('O-07')), o('O-08')])),
    crossDept: f(avgOf([o('O-09'), o('O-10')])),
  };
}

/** 働きがい（Big5 6ファセットの平均）を導出。 */
export function deriveWellbeingV3(scores: Gb74Scores): number {
  const vals = WELLBEING_FACETS.map((key) => {
    const factor = BIG5_FACTORS.find((x) => x.key === key);
    return factor ? getFactorScore(scores, factor) : 0;
  }).filter((v) => v > 0);
  return vals.length === 0 ? NEUTRAL : mean(vals);
}

/** 離職の兆し（R-01〜R-03 の平均）を導出。高いほど良い。 */
export function deriveRetentionV3(retention: Record<string, number> | undefined): number {
  const vals = ['R-01', 'R-02', 'R-03']
    .map((c) => retention?.[c])
    .filter((v): v is number => v != null);
  return vals.length === 0 ? NEUTRAL : mean(vals);
}

/** 1人分をまとめて導出。 */
export function deriveIndividualV3(scores: Gb74Scores): IndividualV3 {
  return {
    wellbeing: deriveWellbeingV3(scores),
    conn: deriveConnectionV3(scores.ona),
    retention: deriveRetentionV3(scores.retention),
  };
}

// ===== 組織集約（仕様書§02）=====

export interface ScoreV3 {
  /** 0〜100 */
  score: number;
  grade: Grade;
  /** 1.0〜5.0 の調整値（検算・部署別表示用） */
  adjusted: number;
}

const makeScore = (adjusted: number): ScoreV3 => {
  const score = toScore100(adjusted);
  return { score, grade: toGrade(score), adjusted };
};

/**
 * 働きがい ＝ 中央値 ×（1 − 低スコア率 × 0.3）。
 * 深刻な少数がいると全体を下げる。
 */
export function aggregateWellbeingV3(individuals: number[]): ScoreV3 {
  const med = median(individuals);
  const lowRate =
    individuals.filter((v) => v <= LOW_SCORE_MAX).length / Math.max(1, individuals.length);
  return makeScore(med * (1 - lowRate * LOW_SCORE_PENALTY));
}

export interface ConnectionResultV3 extends ScoreV3 {
  /** 変数ごとの組織中央値（1.0〜5.0） */
  vars: ConnectionVarsV3;
  /** 最も低い変数。「評価を上げるために」で名指しする */
  weakestVar: keyof ConnectionVarsV3;
}

/**
 * つながり ＝ 4変数の均等平均 × 0.7 ＋ 最低変数 × 0.3。
 * 1つでも弱い変数があると響く。入力は各変数の組織中央値。
 */
export function aggregateConnectionV3(vars: ConnectionVarsV3): ConnectionResultV3 {
  const list = CONNECTION_VAR_KEYS.map((k) => vars[k]);
  const minVal = Math.min(...list);
  const adjusted = mean(list) * CONN_EVEN_WEIGHT + minVal * CONN_MIN_WEIGHT;
  // 同値なら CONNECTION_VAR_KEYS の順で先に来るものを採る（表示順と一致させる）
  const weakestVar = CONNECTION_VAR_KEYS.reduce((a, b) => (vars[a] <= vars[b] ? a : b));
  return { ...makeScore(adjusted), vars, weakestVar };
}

/** 個人の4変数配列から、変数ごとの組織中央値を出す。 */
export function connectionMedians(individuals: ConnectionVarsV3[]): ConnectionVarsV3 {
  const pick = (k: keyof ConnectionVarsV3) => median(individuals.map((c) => c[k]));
  return {
    teamHelp: pick('teamHelp'),
    consultReach: pick('consultReach'),
    workSharing: pick('workSharing'),
    crossDept: pick('crossDept'),
  };
}

/**
 * 離職の兆し ＝ 平均 × 0.6 ＋ 下位25%の平均 × 0.4。
 * 辞めそうな少数を平均に埋もれさせない。
 */
export function aggregateRetentionV3(individuals: number[]): ScoreV3 {
  return makeScore(
    mean(individuals) * RET_MEAN_WEIGHT + bottomQuarterMean(individuals) * RET_BOTTOM_WEIGHT
  );
}

export type ScoreKey = 'wellbeing' | 'connection' | 'retention';

export const SCORE_LABEL: Record<ScoreKey, string> = {
  wellbeing: '働きがい',
  connection: 'つながり',
  retention: '離職の兆し',
};

/** 3スコアの1行説明（モックv3の見出し下）。 */
export const SCORE_SUBLABEL: Record<ScoreKey, string> = {
  wellbeing: '仕事に手ごたえを感じているか',
  connection: '困ったときに頼れる関係があるか',
  retention: '会社に居場所を感じているか',
};

export interface OrgScoresV3 {
  wellbeing: ScoreV3;
  connection: ConnectionResultV3;
  retention: ScoreV3;
  /** GBスコア ＝ 3つの平均。年次比較に使う */
  gbScore: number;
  /**
   * グレード ＝ 最も低い1つで決める（仕様書§03）。
   * GBスコアの平均から出すと、弱いところが他に埋もれる。
   */
  grade: Grade;
  /** 最も低いスコア。「評価を上げるために」の起点 */
  weakest: ScoreKey;
  /** 回答者数 */
  memberCount: number;
}

/** 全メンバーの個人スコアから3スコアとGBスコア・グレードを出す。 */
export function buildOrgScoresV3(individuals: IndividualV3[]): OrgScoresV3 {
  const wellbeing = aggregateWellbeingV3(individuals.map((i) => i.wellbeing));
  const connection = aggregateConnectionV3(connectionMedians(individuals.map((i) => i.conn)));
  const retention = aggregateRetentionV3(individuals.map((i) => i.retention));

  const gbScore = Math.round((wellbeing.score + connection.score + retention.score) / 3);
  const entries: Array<{ key: ScoreKey; s: number }> = [
    { key: 'wellbeing', s: wellbeing.score },
    { key: 'connection', s: connection.score },
    { key: 'retention', s: retention.score },
  ];
  const lowest = entries.reduce((a, b) => (a.s <= b.s ? a : b));

  return {
    wellbeing,
    connection,
    retention,
    gbScore,
    grade: toGrade(lowest.s),
    weakest: lowest.key,
    memberCount: individuals.length,
  };
}
